import VectorImageLayer from 'ol/layer/VectorImage';
import { unByKey } from 'ol/Observable';

import { SketchLayerSource } from 'cadenza/workbook/sketch/sketch-layer/sketch-layer-source';
import { getSketchItemDto, SketchFeatureProperty } from 'cadenza/workbook/sketch/sketch-layer/sketch-types';
import type { Map } from 'cadenza/map/map';
import { LayerProperty } from 'cadenza/map/layer-property';
import { removeFeatureOverlay } from 'cadenza/workbook/sketch/sketch-layer/sketch-style';

import type { EventsKey } from 'ol/events';
import type Overlay from 'ol/Overlay';
import type { ObjectEvent } from 'ol/Object';
import type Feature from 'ol/Feature';

export class SketchLayer extends VectorImageLayer<SketchLayerSource> {

  _map: Map;
  constructor (map: Map, url?: string) {
    super({
      source: new SketchLayerSource(map, url),
      // OL layer renderer is always sorting layer features before rendering. By default the ol.renderer.vector.defaultOrder function is used, which sorts the features by ol_uid.
      // Sketch layer feature order is defined by the order of features in the source.getFeatures() array. But all features also get a zIndex, which is set based on the array order, see SketchLayerSource:change.
      // We cannot prevent OL from resorting of the array, so we define a sorting function considering sketch layer feature order.

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      renderOrder: (feature1: Feature, feature2: Feature) => {
        return getSketchItemDto(feature1).zIndex - getSketchItemDto(feature2).zIndex;
      }
    });
    this._map = map;
    this._registerEventListeners(map);
  }

  // Override the return value, it's always present, never null, as we see in the constructor
  getSource (): SketchLayerSource {
    return super.getSource()!;
  }

  isSilentFeatureCreation () {
    return this.getSource().isSilent();
  }

  _registerEventListeners (map: Map) {
    this.getSource().on('removefeature', ({ feature }) => onRemoveFeature(map, feature));

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.on(`change:${LayerProperty.VISIBLE}`, (event: ObjectEvent) => toggleOverlayElements(map, this.getSource(), event.oldValue === true));

    const olEventKeys = map.getLayers().on('remove', ({ element: layer }) => {
      onLayerRemove(map, this.getSource(), layer === this, olEventKeys);
    });
  }

}

function onRemoveFeature (map: Map, feature?: Feature) {
  if (feature) {
    removeFeatureOverlay(feature, map);
  }
}

function getLayerOverlays (map: Map, source: SketchLayerSource) {
  const overlays: Overlay[] = [];
  source.getFeatures().forEach(feature => {
    const overlayId = feature.get(SketchFeatureProperty.OVERLAY_ID);
    if (overlayId) {
      overlays.push(map.getOverlayById(overlayId));
    }
  });
  return overlays;
}

function toggleOverlayElements (map: Map, source: SketchLayerSource, isHidden: boolean) {
  getLayerOverlays(map, source).forEach(overlay => {
    const overlayElement = overlay.getElement();
    if (overlayElement) {
      overlayElement.hidden = isHidden;
    }
  });
}

function onLayerRemove (map: Map, source: SketchLayerSource, isCurrentLayer: boolean, olEventKeys: EventsKey) {
  if (isCurrentLayer) {
    unByKey(olEventKeys);
    const overlays = getLayerOverlays(map, source);
    overlays.forEach(overlay => map.removeOverlay(overlay));
  }
}
